import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { authService } from '../services/authService';

const Header = ({ onMembershipClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['products', 'wholesale', 'membership'];
      const scrollPosition = window.scrollY + 150; // Offset for fixed header

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(sectionId);
            return;
          }
        }
      }
      setActiveSection('');
    };

    if (location.pathname === '/') {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial position
    } else {
      setActiveSection(''); // Clear active section when not on home
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const navigation = [
    { name: 'HOME', href: '/' },
    {
      name: 'ABOUT',
      hasDropdown: true,
      submenu: [
        { name: 'Our Story', href: '/our-story' },
        { name: 'Our Space', href: '/our-space' },
        { name: 'Our Coffee', href: '/our-coffee' }
      ]
    },
    { name: 'PRODUCT', href: '/#products' },
    { name: 'WHOLESALE', href: '/wholesale' },
    { name: 'MEMBERSHIP', href: '/#membership' }
  ];

  const handleNavClick = (e, item) => {
    // Handle MEMBERSHIP click
    if (item.name === 'MEMBERSHIP') {
      e.preventDefault();

      if (authService.isAuthenticated()) {
        navigate('/member');
      } else if (onMembershipClick) {
        onMembershipClick();
      }
      return;
    }

    // Jika klik HOME dan sudah di home page, scroll ke top
    if (item.href === '/' && location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-[90%] max-w-5xl shadow-2xl border border-white/10 backdrop-blur-md"
      style={{
        backgroundColor: '#31415A',
        borderRadius: mobileMenuOpen ? '1.5rem' : '9999px'
      }}
    >
      <div className="w-full px-4 sm:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link
              to="/"
              className="flex items-center space-x-2"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/maskot-white.png" alt="Iraga Logo" className="w-full h-full object-contain" />
              </div>
              {/* <span className="text-white font-semibold text-lg hidden sm:block">iraga</span> */}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            {navigation.map((item) => {
              // Handle ABOUT dropdown menu
              if (item.hasDropdown) {
                const isActive = item.submenu.some(sub => location.pathname === sub.href);
                return (
                  <div key={item.name} className="relative group"
                    onMouseEnter={() => setAboutDropdownOpen(true)}
                    onMouseLeave={() => setAboutDropdownOpen(false)}
                  >
                    <button
                      className={`text-sm font-medium tracking-wide transition-colors duration-200 flex items-center gap-1 ${isActive
                        ? 'text-white font-bold'
                        : 'text-white/60 hover:text-white'
                        }`}
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {aboutDropdownOpen && (
                      <div
                        className="absolute top-full left-1/2 transform -translate-x-1/2 pt-4 z-[100]"
                        style={{ minWidth: '200px' }}
                      >
                        <div className="bg-white rounded-2xl shadow-2xl py-2 border-2 border-gray-200">
                          {item.submenu.map((sub, index) => (
                            <Link
                              key={sub.name}
                              to={sub.href}
                              className={`block px-6 py-3 text-base font-semibold transition-all duration-200 ${location.pathname === sub.href
                                ? 'bg-[#31415A] text-white'
                                : 'text-[#31415A] hover:bg-gray-300 hover:text-[#31415A]'
                                } ${index === 0 ? 'rounded-t-xl' : ''} ${index === item.submenu.length - 1 ? 'rounded-b-xl' : ''}`}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Handle other menu items
              let isActive = false;

              if (item.href && item.href.includes('#')) {
                // Menu dengan anchor (PRODUCT, WHOLESALE, MEMBERSHIP)
                const [path, sectionId] = item.href.split('#');
                // Active only if on the correct path AND active section matches
                isActive = location.pathname === path && activeSection === sectionId;
              } else if (item.href === '/') {
                // Menu HOME hanya aktif jika di home dan tidak ada activeSection
                isActive = location.pathname === '/' && !activeSection;
              } else if (item.href) {
                isActive = location.pathname === item.href;
              }

              // Special styling untuk MEMBERSHIP
              if (item.name === 'MEMBERSHIP') {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`text-sm font-bold tracking-wide transition-all duration-200 px-4 py-2 rounded-full ${isActive
                      ? 'bg-white text-[#31415A] shadow-lg'
                      : 'bg-white text-[#31415A] hover:shadow-lg hover:scale-105'
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 ${isActive
                    ? 'text-white font-bold' // Brighter and bolder for active state
                    : 'text-white/60 hover:text-white' // Darker/more transparent for inactive state
                    }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-white/10"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/10" style={{ backgroundColor: '#31415A' }}>
          <div className="px-4 py-4 space-y-2">
            {navigation.map((item) => {
              // Handle ABOUT dropdown in mobile
              if (item.hasDropdown) {
                const isActive = item.submenu.some(sub => location.pathname === sub.href);
                return (
                  <div key={item.name} className="space-y-2">
                    <button
                      onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                      className={`w-full px-4 py-3 rounded-lg text-sm font-semibold flex items-center justify-between ${isActive ? 'text-white bg-white/10' : 'text-white/80'}`}
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileAboutOpen && (
                      <div className="ml-4 pl-4 border-l-2 border-white/20 space-y-1">
                        {item.submenu.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.href}
                            className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${location.pathname === sub.href
                              ? 'text-white bg-white/20 shadow-sm'
                              : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              let isActive = false;

              if (item.href.includes('#')) {
                // Menu dengan anchor (PRODUCT, WHOLESALE, MEMBERSHIP)
                const sectionId = item.href.split('#')[1];
                isActive = activeSection === sectionId;
              } else if (item.href === '/') {
                // Menu HOME hanya aktif jika di home dan tidak ada activeSection
                isActive = location.pathname === '/' && !activeSection;
              } else {
                isActive = location.pathname === item.href;
              }

              // Special styling untuk MEMBERSHIP di mobile
              if (item.name === 'MEMBERSHIP') {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 text-center ${isActive
                      ? 'bg-white text-[#31415A] shadow-lg'
                      : 'bg-white text-[#31415A] hover:shadow-lg'
                      }`}
                    onClick={(e) => {
                      handleNavClick(e, item);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${isActive
                    ? 'text-white bg-white/10' // Warna aktif - putih terang
                    : 'text-white/80 hover:bg-white/10 hover:text-white' // Normal dan hover
                    }`}
                  onClick={(e) => {
                    handleNavClick(e, item);
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
